import React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { Ticket } from '@ttstr/api/products';
import { AppState } from '@ttstr/reducers';
import { getProductById } from '@ttstr/reducers/productDetails';
import { useShallowEqualSelector } from '@ttstr/utils';

import TicketMarkup from './TicketMarkup';
import ProductMarkup from './ProductMarkup';

interface RouteParams {
  readonly id: string;
}

/**
 * Auto-injects structured data (JSON-LD) and OG meta tags based on the current route.
 *
 * Place this component inside <ProductDetailsProvider> (and <IntlProvider>) in your App.tsx.
 * It will automatically render <TicketMarkup> or <ProductMarkup> when on a product detail page.
 *
 * No event listeners, no polling — purely reactive to route + Redux state changes.
 */
const AutoSeoMarkup: React.FC = () => {
  const match = useRouteMatch<RouteParams>('/product/:id/:slug?');

  const product = useShallowEqualSelector((state: AppState) => {
    if (!match) return null;
    return getProductById(state, Number(match.params.id));
  });

  if (!match || !product) return null;

  switch (product.type) {
    case 'Ticket':
      return <TicketMarkup ticket={product as Ticket} />;
    default:
      return <ProductMarkup product={product} />;
  }
};

export default React.memo(AutoSeoMarkup);
